import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Modal, Form, Input, Button, message, Spin, Result } from 'antd';
import { CheckCircle, XCircle, Lock } from 'lucide-react';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const [verificationStatus, setVerificationStatus] = useState('verifying');
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setVerificationStatus('invalid');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      console.log('Verifying token:', token);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/confirm-email-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();
      console.log('Verification response:', data);
      
      if (data.status === 'verified') {
        setVerificationStatus('verified');
        setPasswordModalOpen(true);
      } else {
        setVerificationStatus('invalid');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationStatus('invalid');
    }
  };

  const handlePasswordSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          password: values.password,
        }),
      });

      const data = await response.json();
      
      if (data.status === 'password-set') {
        // Redirect with tokens
        if (data.accessToken) {
          message.success('Password set successfully! Redirecting...');
          setTimeout(() => {
            window.location.href = `${process.env.REACT_APP_FIELDOPS_APP_URL}/app/?token=${data.accessToken}&id_token=${data.idToken}&refresh_token=${data.refreshToken}`;
          }, 1500);
        }
      } else {
        if (data.reason === 'invalid-password') {
          message.error('Password must be at least 8 characters with uppercase, lowercase, number, and special character');
        } else {
          message.error('Failed to set password. Please try again.');
        }
      }
    } catch (error) {
      message.error('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const passwordRules = [
    { required: true, message: 'Please enter your password' },
    { min: 8, message: 'Password must be at least 8 characters' },
    {
      // Require at least one lowercase, one uppercase, one digit, and one non-alphanumeric (special) character.
      // Allow any characters otherwise (broadened from a fixed special set).
      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/,
      message: 'Password must contain uppercase, lowercase, number, and special character'
    }
  ];

  if (verificationStatus === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Spin size="large" />
          <p className="mt-4 text-lg text-gray-600">Verifying your email...</p>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Result
          icon={<XCircle className="text-red-500" size={64} />}
          title="Invalid Verification Link"
          subTitle="This verification link is invalid or has expired. Please request a new one."
          extra={
            <Button type="primary" onClick={() => window.location.href = '/'}>
              Back to Home
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Result
        icon={<CheckCircle className="text-green-500" size={64} />}
        title="Email Verified Successfully!"
        subTitle="Your email has been verified. Please set your password to continue."
      />

      <Modal
        title={
          <div className="flex items-center">
            <Lock className="mr-2 text-primary" size={20} />
            Set Your Password
          </div>
        }
        open={passwordModalOpen}
        onCancel={() => setPasswordModalOpen(false)}
        footer={null}
        centered
        width={480}
        closable={false}
        maskClosable={false}
      >
        <div className="py-4">
          <p className="text-gray-600 mb-6">
            Create a secure password to complete your account setup.
          </p>
          
          <Form
            form={form}
            layout="vertical"
            onFinish={handlePasswordSubmit}
            requiredMark={false}
          >
            <Form.Item
              name="password"
              label="Password"
              rules={passwordRules}
            >
              <Input.Password 
                size="large" 
                placeholder="Enter your password"
                className="rounded-lg"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password 
                size="large" 
                placeholder="Confirm your password"
                className="rounded-lg"
              />
            </Form.Item>

            <Form.Item className="mb-0 mt-6">
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
                className="w-full bg-primary border-primary h-12 text-lg font-semibold rounded-lg"
              >
                Set Password & Continue
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default VerifyEmail;
